import React from 'react';
import { useQuery } from '@tanstack/react-query';

import GridWrapper from '../../shared/components/layout/GridWrapper';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';

import company_image from '../../shared/assets/png/company_image.png';
import { getMyData, getProjectsByCompanyId } from '../services/api/implementation/impl';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import Cookies from 'js-cookie';

const Profile = () => {
  const currentUserId = Cookies.get('userId');
  const { collapsed } = useSidebar();

  const { data: user } = useQuery({
    queryKey: ['myProfile'],
    queryFn: getMyData,
    enabled: !!currentUserId
  });

  // Same as EditUser: derive selected companies from user's company mapping
  const selectedCompanies =
    user?.userCompanyMapping?.map((m) => ({
      id: m?.company?.id,
      name: m?.company?.company_name?.trim() || m?.company?.name
    }))?.filter((c) => c?.id != null) || [];

  // Fetch projects for assigned companies (same as EditUser: getProjectsByCompanyId per company)
  const { data: companyProjects = [] } = useQuery({
    queryKey: ['profileProjects', selectedCompanies?.map((c) => c.id)],
    queryFn: async () => {
      if (!selectedCompanies?.length) return [];
      try {
        const projectPromises = selectedCompanies.map(async (company) => {
          const projects = await getProjectsByCompanyId(company.id);
          return projects.map((project) => ({
            ...project,
            company_name: company.name
          }));
        });
        const projectsArrays = await Promise.all(projectPromises);
        return projectsArrays.flat();
      } catch (error) {
        console.error('Error fetching projects for profile:', error);
        return [];
      }
    },
    enabled: !!selectedCompanies?.length
  });

  const transformedDataProjects =
    companyProjects?.map((item) => ({
      name: item.name || '',
      id: item.id,
      displayName: `${item.name || ''} (${item.company_name || ''})`,
      company_id: item.company_id
    })) || [];

  const headerData = [
    { name: 'Phone Number', value: user?.phone_number },
    { name: 'Email Address', value: user?.email },
    { name: 'Vertical', value: user?.designation?.vertical ?? user?.vertical },
    {
      name: 'Designation Level',
      value:
        user?.level === 'Low'
          ? 'Executive Level'
          : user?.level === 'Medium'
            ? 'Manager Level'
            : 'Admin Level'
    },
    { name: 'Designation', value: user?.designation?.name }
  ];

  const assignedCompanies =
    selectedCompanies?.map((c) => c?.name).filter(Boolean) ||
    user?.userCompanyMapping?.map((m) => m?.company?.company_name || m?.company?.name)?.filter(Boolean) ||
    [];
  const assignedProjectIds =
    user?.userProjectMapping?.map((m) => m?.project?.id ?? m?.project_id)?.filter(Boolean) || [];
  const assignedProjects = assignedProjectIds.map((projectId) => {
    const found = transformedDataProjects.find((p) => p.id === projectId || p.id === Number(projectId));
    return found
      ? found.displayName
      : user?.userProjectMapping?.find((m) => (m?.project?.id ?? m?.project_id) === projectId)?.project?.name || String(projectId);
  });

  return (
    <Layout>
        <div
          className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white
    ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'}
  `}>
          <Header data={{ name: `Profile` }} backArrow={true} />
        </div>
        <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
          <GridWrapper width={100}>
            <div className="w-full flex flex-col justify-between items-center gap-8 mt-[6rem]">
              <div className="p-8 rounded-lg bg-white w-full shadow-md flex items-center justify-between gap-6 space-x-10">
                {user?.name ? (
                  <div className="bg-gray-300 w-[12rem] h-[12rem] rounded-full flex justify-center items-center">
                    <span className="text-[8rem] text-white bg-center bg-cover !object-cover !object-center">
                      {user.name[0]}
                    </span>
                  </div>
                ) : (
                  <img
                    src={company_image}
                    alt="company_image"
                    className="w-[12rem] bg-center bg-cover !object-cover !object-center"
                  />
                )}

                <div className="flex-1 flex flex-col gap-8">
                  <div className="flex justify-between items-start">
                    <div className="flex flex-col gap-4">
                      <span className="font-semibold text-xl">{user?.name}</span>
                      <span className="text-gray-400 text-sm">{user?.user_type}</span>
                    </div>
                  </div>

                  <div className="w-[100%] flex justify-between items-center gap-2 flex-wrap">
                    {headerData?.map((item, index) => (
                      <div className="flex flex-col gap-4 flex-shrink-0 min-w-[15%]" key={index}>
                        <span>{item.name}</span>
                        <span className="text-sm">{item.value}</span>
                      </div>
                    ))}
                  </div>

                  <div className="w-full flex flex-col gap-5 mt-2">
                    <div className="flex flex-col gap-2">
                      <span className="text-sm font-medium text-gray-600">Assigned Companies</span>
                      <div className="flex flex-wrap gap-2">
                        {assignedCompanies?.length ? (
                          assignedCompanies.filter(Boolean).map((name, idx) => (
                            <span
                              key={`company-${idx}`}
                              className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-[#2A565E]/10 text-[#2A565E] border border-[#2A565E]/20">
                              {name}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-sm font-medium text-gray-600">Assigned Projects</span>
                      <div className="flex flex-wrap gap-2">
                        {assignedProjects?.length ? (
                          assignedProjects.filter(Boolean).map((name, idx) => (
                            <span
                              key={`project-${idx}`}
                              className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-[#2A565E]/10 text-[#2A565E] border border-[#2A565E]/20">
                              {name}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </GridWrapper>
        </div>
    </Layout>
  );
};

export default Profile;
